/**
 * audio.ts — lightweight WebAudio sound design (no asset files needed).
 *
 * A cozy, always-on soundscape: a slow evolving ambient PAD under the whole session, plus warm
 * procedural SFX (tap / coin / chime / sparkle / completion swells). Everything runs through a
 * gentle master low-pass + soft limiter so stacked voices stay smooth, never harsh or muddy.
 * Respects a global mute (click-free gain ramp) and only starts after a user gesture.
 */

const VOICE_CAP = 14; // max simultaneous SFX voices before new ones are dropped (anti-mud)

class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;   // SFX bus
  private musicGain: GainNode | null = null; // ambient pad bus
  private outGain: GainNode | null = null;   // post-limiter, carries the mute ramp
  private tone_filter: BiquadFilterNode | null = null;
  private _muted = false;
  private chimeStep = 0;
  private voices = 0;
  // ambient pad
  private padOscs: OscillatorNode[] = [];
  private padTimer: ReturnType<typeof setInterval> | null = null;
  private padIdx = 0;
  // pentatonic-ish ascending hook (C D E G A C ...), pleasant + non-dissonant
  private scale = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5, 1174.66];

  // ---- combo / sweep state ----
  // tapTimes is a rolling window of recent paint reveals; combo level is f(density).
  private tapTimes: number[] = [];
  private comboLevel = 0; // 0..1 — drives SFX pitch + gain rise on rapid runs
  // sustained "sweep body" — a low sine that opens while the finger is painting and closes on
  // pointer-up. Gives the continuous-paint feel the audit asked for (vs N discrete pings).
  private sweepOsc: OscillatorNode | null = null;
  private sweepGain: GainNode | null = null;
  // gentle pad voicings (Cmaj9 → Am9 → Fmaj9 → G6) for slow evolution
  private chords = [
    [130.81, 196.0, 329.63, 493.88],
    [110.0, 164.81, 329.63, 440.0],
    [87.31, 174.61, 261.63, 392.0],
    [98.0, 196.0, 293.66, 440.0],
  ];

  get muted(): boolean { return this._muted; }

  setMuted(m: boolean): void {
    this._muted = m;
    const ctx = this.ctx;
    if (this.outGain && ctx) {
      // click-free ramp instead of an instant flip
      const g = this.outGain.gain;
      g.cancelScheduledValues(ctx.currentTime);
      g.setValueAtTime(Math.max(0.0001, g.value), ctx.currentTime);
      g.exponentialRampToValueAtTime(m ? 0.0001 : 1, ctx.currentTime + 0.18);
    }
  }

  toggleMute(): boolean { this.setMuted(!this._muted); return this._muted; }

  private ensure(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AC = window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();

      // out -> soft limiter -> destination  (mute ramp lives on outGain)
      this.outGain = this.ctx.createGain();
      this.outGain.gain.value = this._muted ? 0.0001 : 1;
      const limiter = this.ctx.createDynamicsCompressor();
      limiter.threshold.value = -10; limiter.knee.value = 24;
      limiter.ratio.value = 6; limiter.attack.value = 0.004; limiter.release.value = 0.25;
      this.outGain.connect(limiter);
      limiter.connect(this.ctx.destination);

      // SFX bus: voices -> tone low-pass (takes the harsh edge off bare oscillators) -> master -> out
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.tone_filter = this.ctx.createBiquadFilter();
      this.tone_filter.type = 'lowpass';
      this.tone_filter.frequency.value = 5200;
      this.tone_filter.Q.value = 0.3;
      this.master.connect(this.tone_filter);
      this.tone_filter.connect(this.outGain);

      // ambient pad bus
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.0001;
      this.musicGain.connect(this.outGain);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  /** Call on first pointer/touch to unlock audio.
   *  NOTE: the procedural ambient pad sounded bad (drone-y) so it's OFF by default — a real
   *  recorded loop is the right way to add music later. SFX (tap/coin/chime/celebrate) stay on. */
  unlock(): void {
    this.ensure();
  }

  /** Pause/resume the whole audio graph when the tab is backgrounded. */
  suspend(): void { try { void this.ctx?.suspend(); } catch { /* ignore */ } }
  resume(): void { try { if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume(); } catch { /* ignore */ } }

  // ---------------------------------------------------------------- ambient pad
  startMusic(): void {
    const ctx = this.ensure();
    if (!ctx || !this.musicGain || this.padOscs.length) return;
    const chord = this.chords[this.padIdx % this.chords.length];
    // soft, slowly-breathing low-pass on the pad for movement
    const filt = ctx.createBiquadFilter();
    filt.type = 'lowpass'; filt.frequency.value = 700; filt.Q.value = 0.2;
    filt.connect(this.musicGain);
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.05; lfoGain.gain.value = 260;
    lfo.connect(lfoGain); lfoGain.connect(filt.frequency);
    lfo.start();
    this.padOscs.push(lfo);
    for (let i = 0; i < chord.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = i === 0 ? 'sine' : 'triangle';
      osc.frequency.value = chord[i];
      osc.detune.value = (i - 1.5) * 4; // slight spread for warmth
      const vg = ctx.createGain();
      vg.gain.value = i === 0 ? 0.5 : 0.3;
      osc.connect(vg); vg.connect(filt);
      osc.start();
      this.padOscs.push(osc);
    }
    // fade the bed in, then evolve the chord every ~14s
    this.musicGain.gain.setValueAtTime(0.0001, ctx.currentTime);
    this.musicGain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 4);
    if (!this.padTimer) this.padTimer = setInterval(() => this.evolvePad(), 14000);
  }

  private evolvePad(): void {
    const ctx = this.ctx;
    if (!ctx || this.padOscs.length < 2 || this._muted) return;
    this.padIdx++;
    const chord = this.chords[this.padIdx % this.chords.length];
    // padOscs[0] is the LFO; the chord tones follow
    for (let i = 1; i < this.padOscs.length && i - 1 < chord.length; i++) {
      const osc = this.padOscs[i];
      osc.frequency.cancelScheduledValues(ctx.currentTime);
      osc.frequency.setValueAtTime(osc.frequency.value, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(chord[i - 1], ctx.currentTime + 5);
    }
  }

  stopMusic(): void {
    if (this.padTimer) { clearInterval(this.padTimer); this.padTimer = null; }
    const ctx = this.ctx;
    if (ctx && this.musicGain) this.musicGain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.2);
    const oscs = this.padOscs; this.padOscs = [];
    setTimeout(() => oscs.forEach((o) => { try { o.stop(); } catch { /* already */ } }), 1300);
  }

  // ---------------------------------------------------------------- SFX
  private tone(opts: {
    freq: number; dur: number; type?: OscillatorType; gain?: number;
    attack?: number; delay?: number; detune?: number;
  }): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || this._muted) return;
    if (this.voices >= VOICE_CAP) return; // cap voices so dense bursts don't turn to mush
    const t0 = ctx.currentTime + (opts.delay ?? 0);
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = opts.type ?? 'sine';
    osc.frequency.value = opts.freq;
    if (opts.detune) osc.detune.value = opts.detune;
    const peak = opts.gain ?? 0.18;
    const atk = opts.attack ?? 0.008;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + atk);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
    osc.connect(g);
    g.connect(this.master);
    this.voices++;
    osc.onended = () => { this.voices = Math.max(0, this.voices - 1); };
    osc.start(t0);
    osc.stop(t0 + opts.dur + 0.02);
  }

  /** Soft tap when a cell is committed; small random detune so rapid filling never sounds robotic. */
  tap(): void {
    const d = (Math.random() - 0.5) * 60;
    this.tone({ freq: 300, dur: 0.085, type: 'sine', gain: 0.07, detune: d });
    this.tone({ freq: 470, dur: 0.05, type: 'triangle', gain: 0.035, delay: 0.004, detune: d });
  }

  /**
   * Tell the engine a paint reveal just landed. Builds the rolling combo (density of the last 1.5s)
   * which lifts pitch + density of subsequent `coin()`/`chime()` voices, so a rapid sweep sounds
   * like a rising arpeggio instead of N identical pings. Decays automatically when you stop.
   */
  noteReveal(): void {
    const now = performance.now();
    this.tapTimes.push(now);
    // keep only the last 1500ms
    while (this.tapTimes.length && now - this.tapTimes[0] > 1500) this.tapTimes.shift();
    // map density (0..14 in 1.5s ~ super rapid sweep) -> 0..1
    this.comboLevel = Math.min(1, this.tapTimes.length / 14);
  }

  /** Read the current combo level (decays naturally as taps age out of the window). */
  private combo(): number {
    if (!this.tapTimes.length) return 0;
    const now = performance.now();
    while (this.tapTimes.length && now - this.tapTimes[0] > 1500) this.tapTimes.shift();
    this.comboLevel = Math.min(1, this.tapTimes.length / 14);
    return this.comboLevel;
  }

  // ---- continuous sweep body ----
  // A low sustained tone that opens while the finger is in paint-stroke mode and closes on
  // release. This is the "continuous fabric" that ties the discrete coin pings together so a long
  // sweep feels like one motion, not a staccato burst.
  beginSweep(): void {
    const ctx = this.ensure();
    if (!ctx || !this.master || this._muted) return;
    if (this.sweepOsc) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 130;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.045, ctx.currentTime + 0.06);
    osc.connect(g);
    g.connect(this.master);
    osc.start();
    this.sweepOsc = osc;
    this.sweepGain = g;
  }

  endSweep(): void {
    const ctx = this.ctx;
    if (!ctx || !this.sweepOsc || !this.sweepGain) return;
    const osc = this.sweepOsc, g = this.sweepGain;
    this.sweepOsc = null; this.sweepGain = null;
    g.gain.cancelScheduledValues(ctx.currentTime);
    g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    setTimeout(() => { try { osc.stop(); } catch { /* already */ } }, 220);
  }

  /** Muted "nope" for a mis-tap / dead tap. */
  dud(): void {
    this.tone({ freq: 150, dur: 0.1, type: 'sine', gain: 0.05 });
    this.tone({ freq: 120, dur: 0.12, type: 'triangle', gain: 0.03, delay: 0.03 });
  }

  /** Coin plink for a drip pop; pitch rises slightly with magnitude AND with the rolling combo. */
  coin(magnitude = 0.5): void {
    const c = this.combo();
    const base = 660 + magnitude * 220 + c * 200 + (Math.random() - 0.5) * 30;
    const gain = 0.1 + c * 0.04;
    this.tone({ freq: base, dur: 0.16, type: 'triangle', gain });
    this.tone({ freq: base * 1.5, dur: 0.1, type: 'sine', gain: gain * 0.5, delay: 0.02 });
  }

  /** Ascending chime — each successive cell steps up the scale, resetting per color.
   *  At higher combo levels we add an octave-above shimmer so sweeps sparkle. */
  chime(): void {
    const f = this.scale[this.chimeStep % this.scale.length];
    this.chimeStep++;
    const c = this.combo();
    const gain = 0.11 + c * 0.03;
    this.tone({ freq: f, dur: 0.22, type: 'sine', gain });
    this.tone({ freq: f * 2, dur: 0.14, type: 'sine', gain: gain * 0.4, delay: 0.01 });
    if (c > 0.6) {
      // sparkle layer kicks in only on rapid sweeps
      this.tone({ freq: f * 3, dur: 0.1, type: 'sine', gain: 0.025, delay: 0.02 });
    }
  }

  resetChime(): void { this.chimeStep = 0; }

  /** Sparkle shimmer for special cells. */
  sparkle(): void {
    this.tone({ freq: 1320, dur: 0.18, type: 'sine', gain: 0.06 });
    this.tone({ freq: 1760, dur: 0.14, type: 'sine', gain: 0.05, delay: 0.03 });
    this.tone({ freq: 2200, dur: 0.12, type: 'sine', gain: 0.035, delay: 0.06 });
  }

  /** Color-complete stardust — a warm rising arpeggio. */
  colorComplete(): void {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((f, i) => this.tone({ freq: f, dur: 0.4, type: 'sine', gain: 0.12, delay: i * 0.07 }));
    this.resetChime();
  }

  /** Tiered picture-complete swell. intensity 0..1. */
  celebrate(intensity: number): void {
    const root = [261.63, 329.63, 392.0, 523.25, 659.25, 783.99];
    const count = Math.round(3 + intensity * 3);
    root.slice(0, count).forEach((f, i) => this.tone({
      freq: f, dur: 0.7 + intensity * 0.5, type: 'sine', gain: 0.12 + intensity * 0.06, delay: i * 0.06,
    }));
    this.tone({ freq: 1568, dur: 0.5, type: 'sine', gain: 0.05, delay: 0.18 });
    if (intensity > 0.6) this.tone({ freq: 2093, dur: 0.6, type: 'sine', gain: 0.04, delay: 0.24 });
  }
}

export const Audio = new AudioEngine();
